import { useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
    // Divider, // Not used in KyvernoDetailsContent
    Grid,
    Avatar,
    Stack,
    useTheme, // Keep for transitions
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct
import MiniBot from "../MiniBot"; // Assuming this path is correct

// MODIFIED IMPORT: Import MainDrawer AND its exported width constants
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../MainDrawer"; // Adjust path if needed

// Placeholder for Kyverno Logo
const KYVERNO_LOGO_PATH = "/assets/kyverno.svg"; // UPDATE THIS with your actual path

// --- Content Component (KyvernoDetailsContent) ---
// This is your original KyvernoDetails component, renamed.
// NO UI changes inside this component itself.
const KyvernoDetailsContent: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    const [allKyvernoData, setAllKyvernoData] = useState<any[]>([]);
    const [groupedData, setGroupedData] = useState<any>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
    const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null);
    const [expandedPolicyKey, setExpandedPolicyKey] = useState<string | null>(null);

    const getKyvernoResultsQuery = `
        query GetKyvernoResults {
          getKyvernoResults {
            id
            resourceId
            resourceType
            policies { category description name rules { message name } severity title validationFailureAction }
            cluster { clusterName }
            createdAt
            updatedAt
          }
        }
    `;

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            setAllKyvernoData([]);
            setGroupedData({});
            setExpandedAccordion(false);
            setExpandedClusterId(null);
            setExpandedPolicyKey(null);
            try {
                const response = await queryGraphql(getKyvernoResultsQuery);
                const fetchedData = response?.getKyvernoResults || [];
                if (Array.isArray(fetchedData)) {
                    if (fetchedData.length === 0) {
                        setAllKyvernoData([]);
                        setGroupedData({});
                    } else {
                        setAllKyvernoData(fetchedData);
                        // console.log(fetchedData); // Keep if needed for debugging
                        const groups = fetchedData.reduce((acc, result) => {
                            const type = result?.resourceType;
                            if (type && result) {
                                if (!acc[type]) { acc[type] = []; }
                                acc[type]!.push(result);
                                acc[type]?.sort((a: any, b: any) => {
                                    let timeA = 0, timeB = 0;
                                    const tsA = a?.updatedAt, tsB = b?.updatedAt;
                                    try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || 0); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch { }
                                    try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || 0); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch { }
                                     if (timeA === 0 && typeof tsA === 'string') { try { const dateA = new Date(tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}}
                                     if (timeB === 0 && typeof tsB === 'string') { try { const dateB = new Date(tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}}
                                    return timeB - timeA;
                                });
                            }
                            return acc;
                        }, {} as any);
                        setGroupedData(groups);
                    }
                } else if (response && response.errors) {
                    setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
                } else {
                    setError("Could not fetch Kyverno data or the format is incorrect.");
                }
            } catch (err: any) {
                setError(err.message || "An unexpected error occurred.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedAccordion(isExpanded ? panel : false);
        if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
            setExpandedClusterId(null);
            setExpandedPolicyKey(null);
        }
    };

    const handleClusterSelect = (clusterResultId: string | undefined | null) => {
        if (!clusterResultId) return;
        const newClusterId = expandedClusterId === clusterResultId ? null : clusterResultId;
        setExpandedClusterId(newClusterId);
        if (expandedClusterId !== newClusterId) {
            setExpandedPolicyKey(null);
        }
    };

    const handlePolicyToggle = (policyKey: string | undefined | null) => {
         if (!policyKey) return;
         setExpandedPolicyKey(prevKey => (prevKey === policyKey ? null : policyKey));
    };

    const formatDate = (timestamp: string | number | null | undefined): string => {
        if (timestamp === null || timestamp === undefined) return "N/A";
        let date: Date | null = null;
        try {
            if (typeof timestamp === 'number') { date = new Date(timestamp); }
            else if (typeof timestamp === 'string') {
                const nTs = Number(timestamp);
                 if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { // Check if it's a numeric string representing timestamp
                     date = new Date(nTs);
                 } else {
                     date = new Date(timestamp); // Attempt to parse as date string
                 }
            }
            if (date && !isNaN(date.getTime())) {
                return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
            } else {
                return "Invalid Date";
            }
        } catch (e) {
            return "Invalid Date";
        }
    };

    const formatHeaderDate = (dateObj: Date | null): string => {
        if (!dateObj) return "N/A";
        try {
            if (!isNaN(dateObj.getTime())) {
                return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
            } else { return "Invalid Date"; }
        }
        catch (e) { return "Invalid Date"; }
    }

    const getClusterDisplayName = (scan: any): string => {
        return scan?.cluster?.clusterName ?? scan?.resourceId ?? 'Unknown Cluster';
    };

    const { overallLatestScanDate } = useMemo(() => {
        let latestDateObj: Date | null = null;
        allKyvernoData.forEach(scan => {
            const timestamp = scan?.updatedAt;
            if (timestamp !== null && timestamp !== undefined) {
                 try {
                     let scanDate: Date | null = null;
                     if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
                     else if (typeof timestamp === 'string') {
                         const nTs = Number(timestamp);
                          if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { scanDate = new Date(nTs); }
                          else { scanDate = new Date(timestamp); }
                     }
                     if (scanDate && !isNaN(scanDate.getTime())) {
                         if (!latestDateObj || scanDate > latestDateObj) {
                             latestDateObj = scanDate;
                         }
                     }
                 } catch (e) { /* ignore parsing errors */ }
            }
        });
        return { overallLatestScanDate: latestDateObj };
    }, [allKyvernoData]);

    const getSeverityColor = (severity: string | undefined | null): { color: string, muiColor: 'success' | 'warning' | 'error' | 'info' } => {
        const sev = severity?.toLowerCase();
        if (sev === 'high') return { color: '#D9534F', muiColor: 'error' };
        if (sev === 'medium') return { color: '#F0AD4E', muiColor: 'warning' };
        if (sev === 'low') return { color: '#5CB85C', muiColor: 'success' };
        return { color: theme.palette.grey[500], muiColor: 'info' };
    };

    const getValidationActionChipStyle = (action: string | undefined | null): React.CSSProperties => {
        const act = action?.toLowerCase();
        if (act === 'audit') {
            return { backgroundColor: '#FFF8E1', color: theme.palette.warning.dark };
        }
        if (act === 'enforce') {
             return { backgroundColor: '#FFEBEE', color: theme.palette.error.dark };
        }
        return { backgroundColor: '#eeeeee', color: theme.palette.text.secondary };
    };

    return (
        <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
            <MiniBot/>
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
            
                    <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
                    <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Kyverno </Typography>

                </Breadcrumbs>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" flexDirection="row">
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Kyverno </Typography>
                    </Box>
                     <IconButton
                                                                              aria-label="Help" 
                                                                              onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                                              sx={{
                                                                                padding: 0, 
                                                                                border: 'none',  
                                                                                ml:2,
                                                                                '&:hover': {
                                                                                  
                                                                                  backgroundColor: 'action.hover', 
                                                                                },
                                                                                
                                                                              }}
                                                                            >
                                                                              <Box
                                                                                component="img"
                                                                                src="/assets/Help.svg" 
                                                                                alt="" 
                                                                                sx={{
                                                                                  width: 40,  
                                                                                  height: 40,
                                                                                  display: 'block', 
                                                                                }}
                                                                              />
                                                                            </IconButton>
                </Box>
            </Box>

            <Box sx={{ mx: 'auto' }}>
                {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30} /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Kyverno Data...</Typography></Paper> )}
                {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
                {!isLoading && !error && allKyvernoData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Kyverno policy results are available to display. Ensure Kyverno is running and reporting results. </Typography> </Paper> )}

                {!isLoading && !error && allKyvernoData.length > 0 && (
                    <>
                        <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of policies evaluated by Kyverno. </Typography>
                        <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm="auto"> <Avatar src={KYVERNO_LOGO_PATH} alt="Kyverno Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> K </Avatar> </Grid>
                                <Grid item xs={12} sm>
                                    <Box display="flex" alignItems="center" mb={0.5} >
                                        <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Kyverno </Typography>
                                        <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Kubernetes native policy management for security and compliance. </Typography>
                                </Grid>
                                <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Result Received: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
                            </Grid>
                        </Paper>

                        <Box>
                            {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No Kyverno results found after loading. </Typography> )}
                            {Object.keys(groupedData).length === 1 && groupedData['cluster'] && (
                                <Paper elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", mb: 2.5, overflow: 'hidden' }}>
                                    {(() => {
                                        const results = groupedData['cluster'];
                                        const resourceTypeName = "Clusters";
                                        return (
                                            <>
                                                <Box sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, display: 'flex', alignItems: 'center', borderBottom: results.length > 0 ? '1px solid #eee' : 'none' }}>
                                                    <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
                                                    <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({results.length} result set{results.length !== 1 ? 's' : ''} available) </Typography>
                                                </Box>
                                                <Box sx={{ p: 0, bgcolor: '#fdfdfd' }}>
                                                    <List dense disablePadding>
                                                        {results.map((result: any, index: number) => {
                                                            const isClusterExpanded = expandedClusterId === result?.id;
                                                            const policyCount = result?.policies?.length ?? 0;
                                                            const clusterName = getClusterDisplayName(result);
                                                            const resultDate = formatDate(result?.updatedAt);
                                                            return (
                                                                <React.Fragment key={result?.id ?? `result-${index}`}>
                                                                    <ListItemButton
                                                                        selected={isClusterExpanded}
                                                                        onClick={() => handleClusterSelect(result?.id)}
                                                                        sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 1.5, borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}
                                                                    >
                                                                        <ListItemText
                                                                            primary={clusterName}
                                                                            secondary={`Result Date: ${resultDate}`}
                                                                            primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isClusterExpanded ? 'primary.main' : 'text.primary' }}
                                                                            secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
                                                                        />
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <Chip label={`${policyCount} Policies`} size="small" sx={{ fontFamily:'Gilroy-Regular', height: '24px', bgcolor: '#e0e0e0', color: '#333' }}/>
                                                                            <ExpandMoreIcon sx={{ transform: isClusterExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'action.active' }} />
                                                                        </Stack>
                                                                    </ListItemButton>
                                                                    <Collapse in={isClusterExpanded} timeout="auto" unmountOnExit>
                                                                        <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}>
                                                                            <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2.5 }}> Policies Evaluated in: {clusterName} </Typography>
                                                                             {(result?.policies?.length ?? 0) > 0 ? (
                                                                                 result.policies.map((policy: any, policyIndex: number) => {
                                                                                     const policyKey = `${result.id}-${policy.name}`;
                                                                                     const isPolicyExpanded = expandedPolicyKey === policyKey;
                                                                                     const severityInfo = getSeverityColor(policy.severity);
                                                                                     const validationActionStyle = getValidationActionChipStyle(policy.validationFailureAction);
                                                                                     const ruleCount = policy?.rules?.length ?? 0;
                                                                                     return (
                                                                                         <Accordion key={policyKey} expanded={isPolicyExpanded} onChange={() => handlePolicyToggle(policyKey)} disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '6px !important', boxShadow: 'none', '&:before': { display: 'none' }, mb: 1.5, overflow: 'hidden', bgcolor: isPolicyExpanded ? '#f9f9f9' : 'transparent' }}>
                                                                                             <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${policyKey}-content`} id={`${policyKey}-header`} sx={{ px: 2, py: 0.5, minHeight: '48px', '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 } }}>
                                                                                                 <Box sx={{ flexGrow: 1, mr: 1 }}>
                                                                                                     <Typography fontFamily="Gilroy-Medium" fontSize="0.9rem" component="div"> {policy.name} <Typography component="span" sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', ml: 0.75 }}> ({ruleCount} Rule{ruleCount !== 1 ? 's' : ''}) </Typography> </Typography>
                                                                                                     <Typography fontFamily="Gilroy-Regular" fontSize="0.8rem" color="text.secondary" component="div">{policy.title}</Typography>
                                                                                                 </Box>
                                                                                                 <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                                                                                                     <Tooltip title={`Severity: ${policy.severity?.toUpperCase() ?? 'N/A'}`} placement="top">
                                                                                                         <Chip label={policy.severity?.toUpperCase() ?? 'N/A'} size="small" sx={{ bgcolor: severityInfo.color, color: '#fff', height: '28px', fontSize: '0.8rem', fontFamily: 'Gilroy-SemiBold', borderRadius: '16px', '& .MuiChip-label': { pl: '12px', pr: '12px' } }} />
                                                                                                     </Tooltip>
                                                                                                     <Tooltip title={`Action on Failure: ${policy.validationFailureAction ?? 'N/A'}`} placement="top">
                                                                                                         <Chip label={policy.validationFailureAction ?? 'N/A'} icon={<InfoOutlinedIcon sx={{ fontSize: '1rem', ml: '6px', mr: '-4px' }} />} size="small" style={validationActionStyle} sx={{ height: '22px', fontSize: '0.75rem', fontFamily: 'Gilroy-Regular', border: 'none', '& .MuiChip-icon': { color: validationActionStyle.color } }}/>
                                                                                                     </Tooltip>
                                                                                                 </Stack>
                                                                                             </AccordionSummary>
                                                                                             <AccordionDetails sx={{ px: 2, pt: 1, pb: 2, borderTop: '1px solid #eee' }}>
                                                                                                 <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', color: 'text.secondary', mb: 2, whiteSpace: 'pre-wrap' }}> {policy.description || 'No description provided.'} </Typography>
                                                                                                 <Typography variant="subtitle2" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.85rem', mb: 1 }}> Rules: </Typography>
                                                                                                 <List dense disablePadding sx={{ border: '1px solid #eee', borderRadius: '4px', bgcolor: '#fff' }}>
                                                                                                     {(policy?.rules?.length ?? 0) > 0 ? ( policy.rules.map((rule: any, ruleIndex: number) => ( <ListItem key={`${policyKey}-rule-${rule.name ?? ruleIndex}`} divider={ruleIndex < (policy.rules.length - 1)}> <ListItemText primary={rule.name || 'Unnamed Rule'} secondary={rule.message || 'No message provided.'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> </ListItem> )) ) : ( <ListItem><ListItemText primary="No specific rules listed for this policy." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.8rem' }} /></ListItem> )}
                                                                                                 </List>
                                                                                             </AccordionDetails>
                                                                                         </Accordion>
                                                                                     );
                                                                                 })
                                                                            ) : ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem', mt: 2, mb: 1 }}> No policies found in this result set. </Typography> )}
                                                                        </Box>
                                                                    </Collapse>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </List>
                                                </Box>
                                            </>
                                        );
                                    })()}
                                </Paper>
                            )}
                            {/* Case 2 (Accordion for multiple resource types) would go here if needed */}
                        </Box>
                    </>
                )}
            </Box>
        </Container>
    );
};


// --- Parent Layout Component (KyvernoDetailsPage) ---
const KyvernoDetails: React.FC = () => {
    const theme = useTheme();

    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
    };

    // Using your remembered calculation for marginLeft
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                // Assuming "integrations" is the correct index for Kyverno page
                // If Kyverno has its own top-level menu item, update this index.
                index={"integrations"} // Or a more specific index like "integrations-kyverno" if applicable
                onStateChange={handleDrawerStateChange}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    // bgcolor: '#fafafa', // bgcolor commented out as per your previous change
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    // Using your remembered unit and calculation for marginLeft
                    marginLeft: `${currentMarginLeft}vh`,
                }}
            >
                <KyvernoDetailsContent />
            </Box>
        </Box>
    );
};

export default KyvernoDetails;




// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     // Divider,
//     Grid,
//     Avatar,
//     Stack,
//     useTheme // Import useTheme to access theme colors
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For Validation Action tooltip
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct
// import MiniBot from "../MiniBot";

// // --- Placeholder for Kyverno Logo ---
// const KYVERNO_LOGO_PATH = "/assets/kyverno.svg"; // UPDATE THIS with your actual path

// // --- Kyverno Component ---
// const KyvernoDetails: React.FC = () => {
//     const navigate = useNavigate();
//     const theme = useTheme(); // Access theme for potential color overrides

//     // --- State Variables ---
//     const [allKyvernoData, setAllKyvernoData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({}); // Grouped by resourceType ('cluster')
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     // CHANGE: expandedAccordion state is kept for potential future resource types, but less critical now
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null); // For individual cluster result
//     const [expandedPolicyKey, setExpandedPolicyKey] = useState<string | null>(null); // For individual policy within a cluster result (e.g., "clusterId-policyName")

//     // --- GraphQL Query ---
//     const getKyvernoResultsQuery = `
//         query GetKyvernoResults {
//           getKyvernoResults {
//             id
//             resourceId
//             resourceType
//             policies { category description name rules { message name } severity title validationFailureAction }
//             cluster { clusterName }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllKyvernoData([]);
//             setGroupedData({});
//             setExpandedAccordion(false); // Reset accordion state
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//             try {
//                 const response = await queryGraphql(getKyvernoResultsQuery);
//                 const fetchedData = response?.getKyvernoResults || [];
//                 if (Array.isArray(fetchedData)) {
//                     if (fetchedData.length === 0) {
//                         setAllKyvernoData([]);
//                         setGroupedData({});
//                     } else {
//                         setAllKyvernoData(fetchedData);
//                         console.log(fetchedData);
//                         // Group by resourceType
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort by update time within the group (most recent first)
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || 0); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch { }
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || 0); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch { }
//                                      if (timeA === 0 && typeof tsA === 'string') { try { const dateA = new Date(tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}}
//                                      if (timeB === 0 && typeof tsB === 'string') { try { const dateB = new Date(tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}}
//                                     return timeB - timeA; // Descending
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                         // CHANGE: No need to auto-expand 'cluster' accordion if it's static
//                     }
//                 } else if (response && response.errors) {
//                     setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 } else {
//                     setError("Could not fetch Kyverno data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 setError(err.message || "An unexpected error occurred.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // --- Event Handlers ---
//     // CHANGE: handleAccordionChange only relevant if multiple resource types exist
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//         }
//     };

//     // This handler remains crucial for expanding/collapsing individual cluster results
//     const handleClusterSelect = (clusterResultId: string | undefined | null) => {
//         if (!clusterResultId) return;
//         const newClusterId = expandedClusterId === clusterResultId ? null : clusterResultId;
//         setExpandedClusterId(newClusterId);
//         if (expandedClusterId !== newClusterId) {
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handlePolicyToggle = (policyKey: string | undefined | null) => {
//          if (!policyKey) return;
//          setExpandedPolicyKey(prevKey => (prevKey === policyKey ? null : policyKey));
//     };

//     // --- Helper Functions (unchanged from previous version) ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//             if (typeof timestamp === 'number') { date = new Date(timestamp); }
//             else if (typeof timestamp === 'string') {
//                 const nTs = Number(timestamp);
//                  if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) {
//                      date = new Date(nTs);
//                  } else {
//                      date = new Date(timestamp);
//                  }
//             }
//             if (date && !isNaN(date.getTime())) {
//                 return date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//             } else {
//                 return "Invalid Date";
//             }
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//         try {
//             if (!isNaN(dateObj.getTime())) {
//                 return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//             } else { return "Invalid Date"; }
//         }
//         catch (e) { return "Invalid Date"; }
//     }

//     const getClusterDisplayName = (scan: any): string => {
//         return scan?.cluster?.clusterName ?? scan?.resourceId ?? 'Unknown Cluster';
//     };

//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allKyvernoData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//             if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                           if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { scanDate = new Date(nTs); }
//                           else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) {
//                              latestDateObj = scanDate;
//                          }
//                      }
//                  } catch (e) { /* ignore parsing errors */ }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allKyvernoData]);

//     const getSeverityColor = (severity: string | undefined | null): { color: string, muiColor: 'success' | 'warning' | 'error' | 'info' } => {
//         const sev = severity?.toLowerCase();
//         if (sev === 'high') return { color: '#D9534F', muiColor: 'error' };
//         if (sev === 'medium') return { color: '#F0AD4E', muiColor: 'warning' };
//         if (sev === 'low') return { color: '#5CB85C', muiColor: 'success' };
//         return { color: theme.palette.grey[500], muiColor: 'info' };
//     };

//     const getValidationActionChipStyle = (action: string | undefined | null): React.CSSProperties => {
//         const act = action?.toLowerCase();
//         if (act === 'audit') {
//             return { backgroundColor: '#FFF8E1', color: theme.palette.warning.dark };
//         }
//         if (act === 'enforce') {
//              return { backgroundColor: '#FFEBEE', color: theme.palette.error.dark };
//         }
//         return { backgroundColor: '#eeeeee', color: theme.palette.text.secondary };
//     };
//     // --- End Helper Functions ---

//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//             <MiniBot/>
//             <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Kyverno </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Kyverno </Typography>
//                 </Box>
//             </Box>

//             {/* Content Area */}
//             <Box sx={{  mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30} /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Kyverno Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allKyvernoData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Kyverno policy results are available to display. Ensure Kyverno is running and reporting results. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allKyvernoData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of policies evaluated by Kyverno. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             {/* Header Card Content (unchanged) */}
//                             <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={KYVERNO_LOGO_PATH} alt="Kyverno Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> K </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Kyverno </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Kubernetes native policy management for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Result Received: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* --- CHANGE: Conditional Rendering for Cluster Section --- */}
//                         <Box>
//                             {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No Kyverno results found after loading. </Typography> )}

//                             {/* Case 1: Only 'cluster' results exist -> Render static container */}
//                             {Object.keys(groupedData).length === 1 && groupedData['cluster'] && (
//                                 <Paper elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", mb: 2.5, overflow: 'hidden' }}>
//                                     {(() => { // Immediately invoked function to access results easily
//                                         const results = groupedData['cluster'];
//                                         const resourceTypeName = "Clusters";
//                                         return (
//                                             <>
//                                                 {/* Static Header for Clusters */}
//                                                 <Box sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, display: 'flex', alignItems: 'center', borderBottom: results.length > 0 ? '1px solid #eee' : 'none' }}>
//                                                     <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                                     <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({results.length} result set{results.length !== 1 ? 's' : ''} available) </Typography>
//                                                 </Box>
//                                                 {/* List of Cluster Results (Remains collapsible internally) */}
//                                                 <Box sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                                     <List dense disablePadding>
//                                                         {results.map((result: any, index: number) => {
//                                                             const isClusterExpanded = expandedClusterId === result?.id;
//                                                             const policyCount = result?.policies?.length ?? 0;
//                                                             const clusterName = getClusterDisplayName(result);
//                                                             const resultDate = formatDate(result?.updatedAt);

//                                                             return (
//                                                                 <React.Fragment key={result?.id ?? `result-${index}`}>
//                                                                     {/* Clickable Item for each Cluster Result (toggles Collapse below) */}
//                                                                     <ListItemButton
//                                                                         selected={isClusterExpanded}
//                                                                         onClick={() => handleClusterSelect(result?.id)}
//                                                                         sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 1.5, borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}
//                                                                     >
//                                                                         <ListItemText
//                                                                             primary={clusterName}
//                                                                             secondary={`Result Date: ${resultDate}`}
//                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isClusterExpanded ? 'primary.main' : 'text.primary' }}
//                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                         />
//                                                                         {/* Display policy count and arrow icon */}
//                                                                         <Stack direction="row" spacing={1} alignItems="center">
//                                                                             <Chip label={`${policyCount} Policies`} size="small" sx={{ fontFamily:'Gilroy-Regular', height: '24px', bgcolor: '#e0e0e0', color: '#333' }}/>
//                                                                             <ExpandMoreIcon sx={{ transform: isClusterExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'action.active' }} />
//                                                                         </Stack>
//                                                                     </ListItemButton>

//                                                                     {/* Collapsible Detail Section for Policies */}
//                                                                     <Collapse in={isClusterExpanded} timeout="auto" unmountOnExit>
//                                                                         {/* Policy Accordions render here (unchanged) */}
//                                                                         <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}>
//                                                                             <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2.5 }}> Policies Evaluated in: {clusterName} </Typography>
//                                                                              {(result?.policies?.length ?? 0) > 0 ? (
//                                                                                  result.policies.map((policy: any, policyIndex: number) => {
//                                                                                      const policyKey = `${result.id}-${policy.name}`;
//                                                                                      const isPolicyExpanded = expandedPolicyKey === policyKey;
//                                                                                      const severityInfo = getSeverityColor(policy.severity);
//                                                                                      const validationActionStyle = getValidationActionChipStyle(policy.validationFailureAction);
//                                                                                      const ruleCount = policy?.rules?.length ?? 0;

//                                                                                      return (
//                                                                                          <Accordion key={policyKey} expanded={isPolicyExpanded} onChange={() => handlePolicyToggle(policyKey)} disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '6px !important', boxShadow: 'none', '&:before': { display: 'none' }, mb: 1.5, overflow: 'hidden', bgcolor: isPolicyExpanded ? '#f9f9f9' : 'transparent' }}>
//                                                                                              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${policyKey}-content`} id={`${policyKey}-header`} sx={{ px: 2, py: 0.5, minHeight: '48px', '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 } }}>
//                                                                                                  <Box sx={{ flexGrow: 1, mr: 1 }}>
//                                                                                                      <Typography fontFamily="Gilroy-Medium" fontSize="0.9rem" component="div"> {policy.name} <Typography component="span" sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', ml: 0.75 }}> ({ruleCount} Rule{ruleCount !== 1 ? 's' : ''}) </Typography> </Typography>
//                                                                                                      <Typography fontFamily="Gilroy-Regular" fontSize="0.8rem" color="text.secondary" component="div">{policy.title}</Typography>
//                                                                                                  </Box>
//                                                                                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
//                                                                                                      <Tooltip title={`Severity: ${policy.severity?.toUpperCase() ?? 'N/A'}`} placement="top">
//                                                                                                          <Chip label={policy.severity?.toUpperCase() ?? 'N/A'} size="small" sx={{ bgcolor: severityInfo.color, color: '#fff', height: '28px', fontSize: '0.8rem', fontFamily: 'Gilroy-SemiBold', borderRadius: '16px', '& .MuiChip-label': { pl: '12px', pr: '12px' } }} />
//                                                                                                      </Tooltip>
//                                                                                                      <Tooltip title={`Action on Failure: ${policy.validationFailureAction ?? 'N/A'}`} placement="top">
//                                                                                                          <Chip label={policy.validationFailureAction ?? 'N/A'} icon={<InfoOutlinedIcon sx={{ fontSize: '1rem', ml: '6px', mr: '-4px' }} />} size="small" style={validationActionStyle} sx={{ height: '22px', fontSize: '0.75rem', fontFamily: 'Gilroy-Regular', border: 'none', '& .MuiChip-icon': { color: validationActionStyle.color } }}/>
//                                                                                                      </Tooltip>
//                                                                                                  </Stack>
//                                                                                              </AccordionSummary>
//                                                                                              <AccordionDetails sx={{ px: 2, pt: 1, pb: 2, borderTop: '1px solid #eee' }}>
//                                                                                                  <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', color: 'text.secondary', mb: 2, whiteSpace: 'pre-wrap' }}> {policy.description || 'No description provided.'} </Typography>
//                                                                                                  <Typography variant="subtitle2" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.85rem', mb: 1 }}> Rules: </Typography>
//                                                                                                  <List dense disablePadding sx={{ border: '1px solid #eee', borderRadius: '4px', bgcolor: '#fff' }}>
//                                                                                                      {(policy?.rules?.length ?? 0) > 0 ? ( policy.rules.map((rule: any, ruleIndex: number) => ( <ListItem key={`${policyKey}-rule-${rule.name ?? ruleIndex}`} divider={ruleIndex < (policy.rules.length - 1)}> <ListItemText primary={rule.name || 'Unnamed Rule'} secondary={rule.message || 'No message provided.'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> </ListItem> )) ) : ( <ListItem><ListItemText primary="No specific rules listed for this policy." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.8rem' }} /></ListItem> )}
//                                                                                                  </List>
//                                                                                              </AccordionDetails>
//                                                                                          </Accordion>
//                                                                                      );
//                                                                                  })
//                                                                             ) : ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem', mt: 2, mb: 1 }}> No policies found in this result set. </Typography> )}
//                                                                         </Box>
//                                                                     </Collapse>
//                                                                 </React.Fragment>
//                                                             );
//                                                         })}
//                                                     </List>
//                                                 </Box>
//                                             </>
//                                         );
//                                     })()}
//                                 </Paper>
//                             )}

//                             {/* Case 2: Multiple resource types exist -> Render Accordion for each type (Fallback) */}
                            
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default KyvernoDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     // Divider,
//     Grid,
//     Avatar,
//     Stack,
//     useTheme // Import useTheme to access theme colors
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For Validation Action tooltip
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Placeholder for Kyverno Logo ---
// const KYVERNO_LOGO_PATH = "/assets/kyverno.svg"; // UPDATE THIS with your actual path

// // --- Kyverno Component ---
// const KyvernoDetails: React.FC = () => {
//     const navigate = useNavigate();
//     const theme = useTheme(); // Access theme for potential color overrides

//     // --- State Variables ---
//     const [allKyvernoData, setAllKyvernoData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({}); // Grouped by resourceType ('cluster')
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     // CHANGE: expandedAccordion state is kept for potential future resource types, but less critical now
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
//     const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null); // For individual cluster result
//     const [expandedPolicyKey, setExpandedPolicyKey] = useState<string | null>(null); // For individual policy within a cluster result (e.g., "clusterId-policyName")

//     // --- GraphQL Query ---
//     const getKyvernoResultsQuery = `
//         query GetKyvernoResults {
//           getKyvernoResults {
//             id
//             resourceId
//             resourceType
//             policies { category description name rules { message name } severity title validationFailureAction }
//             cluster { clusterName }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllKyvernoData([]);
//             setGroupedData({});
//             setExpandedAccordion(false); // Reset accordion state
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//             try {
//                 const response = await queryGraphql(getKyvernoResultsQuery);
//                 const fetchedData = response?.getKyvernoResults || [];
//                 if (Array.isArray(fetchedData)) {
//                     if (fetchedData.length === 0) {
//                         setAllKyvernoData([]);
//                         setGroupedData({});
//                     } else {
//                         setAllKyvernoData(fetchedData);
//                         console.log("Fetched Kyverno Data:", fetchedData); // Log fetched data
//                         // Group by resourceType
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) { // Ensure result is not null/undefined
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort by update time within the group (most recent first)
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     // Robust date parsing (handles numbers, parsable strings)
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || 0); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch { }
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || 0); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch { }
//                                      if (timeA === 0 && typeof tsA === 'string') { try { const dateA = new Date(tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}}
//                                      if (timeB === 0 && typeof tsB === 'string') { try { const dateB = new Date(tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}}
//                                     return timeB - timeA; // Descending
//                                 });
//                             } else {
//                                 console.warn("Skipping result during grouping due to missing resourceType or null result:", result);
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                         // CHANGE: No need to auto-expand 'cluster' accordion if it's static
//                     }
//                 } else if (response && response.errors) {
//                     setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 } else {
//                     setError("Could not fetch Kyverno data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 setError(err.message || "An unexpected error occurred.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []);

//     // --- Event Handlers ---
//     // CHANGE: handleAccordionChange only relevant if multiple resource types exist
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//         }
//     };

//     // This handler remains crucial for expanding/collapsing individual cluster results
//     const handleClusterSelect = (clusterResultId: string | undefined | null) => {
//         if (!clusterResultId) return;
//         const newClusterId = expandedClusterId === clusterResultId ? null : clusterResultId;
//         setExpandedClusterId(newClusterId);
//         if (expandedClusterId !== newClusterId) { // Reset policy if cluster changes
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handlePolicyToggle = (policyKey: string | undefined | null) => {
//          if (!policyKey) return;
//          setExpandedPolicyKey(prevKey => (prevKey === policyKey ? null : policyKey));
//     };

//     // --- Helper Functions (unchanged) ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//             if (typeof timestamp === 'number') { date = new Date(timestamp); }
//             else if (typeof timestamp === 'string') {
//                 const nTs = Number(timestamp);
//                  if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) {
//                      date = new Date(nTs); // Handle numeric strings representing timestamps
//                  } else {
//                      date = new Date(timestamp); // Handle standard date strings
//                  }
//             }
//             if (date && !isNaN(date.getTime())) {
//                 return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
//             } else {
//                 // console.warn("formatDate received invalid date input:", timestamp);
//                 return "Invalid Date";
//             }
//         } catch (e) {
//             // console.error("Error parsing date in formatDate:", e);
//             return "Invalid Date";
//         }
//     };

//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//         try {
//             if (!isNaN(dateObj.getTime())) {
//                 return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//             } else { return "Invalid Date"; }
//         }
//         catch (e) { return "Invalid Date"; }
//     }

//     const getClusterDisplayName = (scan: any): string => {
//         // Prioritize cluster name, then resourceId, then fallback
//         return scan?.cluster?.clusterName ?? scan?.resourceId ?? 'Unknown Cluster';
//     };

//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allKyvernoData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//             if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                           if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { scanDate = new Date(nTs); }
//                           else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) {
//                              latestDateObj = scanDate;
//                          }
//                      }
//                  } catch (e) { /* ignore parsing errors during calculation */ }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allKyvernoData]);

//     const getSeverityColor = (severity: string | undefined | null): { color: string, muiColor: 'success' | 'warning' | 'error' | 'info' } => {
//         const sev = severity?.toLowerCase();
//         if (sev === 'high') return { color: '#D9534F', muiColor: 'error' };
//         if (sev === 'medium') return { color: '#F0AD4E', muiColor: 'warning' };
//         if (sev === 'low') return { color: '#5CB85C', muiColor: 'success' };
//         return { color: theme.palette.grey[500], muiColor: 'info' };
//     };

//     const getValidationActionChipStyle = (action: string | undefined | null): React.CSSProperties => {
//         const act = action?.toLowerCase();
//         if (act === 'audit') {
//                         return { backgroundColor: '#FFF8E1', color: theme.palette.warning.dark };
//                     }
//                     if (act === 'enforce') {
//                          return { backgroundColor: '#FFEBEE', color: theme.palette.error.dark };
//                     }
//                     return { backgroundColor: '#eeeeee', color: theme.palette.text.secondary };
//     };
//     // --- End Helper Functions ---

//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//             <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Kyverno </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Kyverno </Typography>
//                 </Box>
//             </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30} /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Kyverno Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allKyvernoData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Kyverno policy results are available to display. Ensure Kyverno is running and reporting results. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allKyvernoData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of policies evaluated by Kyverno. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             {/* Header Card Content (unchanged) */}
//                             <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={KYVERNO_LOGO_PATH} alt="Kyverno Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> K </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Kyverno </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Kubernetes native policy management for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Result Received: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* --- Results Section --- */}
//                         <Box>
//                             {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No Kyverno results found after loading. </Typography> )}

//                             {/* Case 1: Only 'cluster' results exist -> Render static container */}
//                             {Object.keys(groupedData).length === 1 && groupedData['cluster'] && (
//                                 <Paper elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px", mb: 2.5, overflow: 'hidden' }}>
//                                     {(() => { // Immediately invoked function to access results easily
//                                         const results = groupedData['cluster'];
//                                         const resourceTypeName = "Clusters";
//                                         return (
//                                             <>
//                                                 {/* Static Header for Clusters */}
//                                                 <Box sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, display: 'flex', alignItems: 'center', borderBottom: results.length > 0 ? '1px solid #eee' : 'none' }}>
//                                                     <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                                     <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({results.length} result set{results.length !== 1 ? 's' : ''} available) </Typography>
//                                                 </Box>
//                                                 {/* List of Cluster Results (Remains collapsible internally) */}
//                                                 <Box sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                                     <List dense disablePadding>
//                                                         {results.map((result: any, index: number) => {
//                                                             // --- *** CHANGE START: Added check for result.id *** ---
//                                                             if (!result?.id) {
//                                                                 console.warn("Skipping rendering Kyverno result due to missing ID:", result);
//                                                                 return null; // Skip this item if ID is missing
//                                                             }
//                                                             // --- *** CHANGE END *** ---

//                                                             const isClusterExpanded = expandedClusterId === result.id; // Use result.id safely now
//                                                             const policyCount = result?.policies?.length ?? 0;
//                                                             const clusterName = getClusterDisplayName(result);
//                                                             const resultDate = formatDate(result?.updatedAt);

//                                                             return (
//                                                                 // Use result.id safely as key
//                                                                 <React.Fragment key={result.id}>
//                                                                     {/* Clickable Item for each Cluster Result (toggles Collapse below) */}
//                                                                     <ListItemButton
//                                                                         selected={isClusterExpanded}
//                                                                         onClick={() => handleClusterSelect(result.id)} // Use result.id safely
//                                                                         sx={{ '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } }, px: { xs: 2, md: 3 }, py: 1.5, borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}
//                                                                     >
//                                                                         <ListItemText
//                                                                             primary={clusterName}
//                                                                             secondary={`Result Date: ${resultDate}`}
//                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isClusterExpanded ? 'primary.main' : 'text.primary' }}
//                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                         />
//                                                                         {/* Display policy count and arrow icon */}
//                                                                         <Stack direction="row" spacing={1} alignItems="center">
//                                                                             <Chip label={`${policyCount} Policies`} size="small" sx={{ fontFamily:'Gilroy-Regular', height: '24px', bgcolor: '#e0e0e0', color: '#333' }}/>
//                                                                             <ExpandMoreIcon sx={{ transform: isClusterExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'action.active' }} />
//                                                                         </Stack>
//                                                                     </ListItemButton>

//                                                                     {/* Collapsible Detail Section for Policies */}
//                                                                     <Collapse in={isClusterExpanded} timeout="auto" unmountOnExit>
//                                                                         {/* Policy Accordions render here (unchanged) */}
//                                                                         <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}>
//                                                                             <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2.5 }}> Policies Evaluated in: {clusterName} </Typography>
//                                                                              {(result?.policies?.length ?? 0) > 0 ? (
//                                                                                  result.policies.map((policy: any, policyIndex: number) => {
//                                                                                      // Use result.id safely in key
//                                                                                      const policyKey = `${result.id}-${policy.name}`;
//                                                                                      const isPolicyExpanded = expandedPolicyKey === policyKey;
//                                                                                      const severityInfo = getSeverityColor(policy.severity);
//                                                                                      const validationActionStyle = getValidationActionChipStyle(policy.validationFailureAction);
//                                                                                      const ruleCount = policy?.rules?.length ?? 0;

//                                                                                      return (
//                                                                                          <Accordion key={policyKey} expanded={isPolicyExpanded} onChange={() => handlePolicyToggle(policyKey)} disableGutters elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: '6px !important', boxShadow: 'none', '&:before': { display: 'none' }, mb: 1.5, overflow: 'hidden', bgcolor: isPolicyExpanded ? '#f9f9f9' : 'transparent' }}>
//                                                                                              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${policyKey}-content`} id={`${policyKey}-header`} sx={{ px: 2, py: 0.5, minHeight: '48px', '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 } }}>
//                                                                                                  <Box sx={{ flexGrow: 1, mr: 1 }}>
//                                                                                                      <Typography fontFamily="Gilroy-Medium" fontSize="0.9rem" component="div"> {policy.name || 'Unnamed Policy'} <Typography component="span" sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', ml: 0.75 }}> ({ruleCount} Rule{ruleCount !== 1 ? 's' : ''}) </Typography> </Typography>
//                                                                                                      <Typography fontFamily="Gilroy-Regular" fontSize="0.8rem" color="text.secondary" component="div">{policy.title || 'No title'}</Typography>
//                                                                                                  </Box>
//                                                                                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
//                                                                                                      <Tooltip title={`Severity: ${policy.severity?.toUpperCase() ?? 'N/A'}`} placement="top">
//                                                                                                          <Chip label={policy.severity?.toUpperCase() ?? 'N/A'} size="small" sx={{ bgcolor: severityInfo.color, color: '#fff', height: '28px', fontSize: '0.8rem', fontFamily: 'Gilroy-SemiBold', borderRadius: '16px', '& .MuiChip-label': { pl: '12px', pr: '12px' } }} />
//                                                                                                      </Tooltip>
//                                                                                                      <Tooltip title={`Action on Failure: ${policy.validationFailureAction ?? 'N/A'}`} placement="top">
//                                                                                                          <Chip label={policy.validationFailureAction ?? 'N/A'} icon={<InfoOutlinedIcon sx={{ fontSize: '1rem', ml: '6px', mr: '-4px' }} />} size="small" style={validationActionStyle} sx={{ height: '22px', fontSize: '0.75rem', fontFamily: 'Gilroy-Regular', border: 'none', '& .MuiChip-icon': { color: validationActionStyle.color } }}/>
//                                                                                                      </Tooltip>
//                                                                                                  </Stack>
//                                                                                              </AccordionSummary>
//                                                                                              <AccordionDetails sx={{ px: 2, pt: 1, pb: 2, borderTop: '1px solid #eee' }}>
//                                                                                                  <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', color: 'text.secondary', mb: 2, whiteSpace: 'pre-wrap' }}> {policy.description || 'No description provided.'} </Typography>
//                                                                                                  <Typography variant="subtitle2" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.85rem', mb: 1 }}> Rules: </Typography>
//                                                                                                  <List dense disablePadding sx={{ border: '1px solid #eee', borderRadius: '4px', bgcolor: '#fff' }}>
//                                                                                                      {(policy?.rules?.length ?? 0) > 0 ? ( policy.rules.map((rule: any, ruleIndex: number) => ( <ListItem key={`${policyKey}-rule-${rule.name ?? ruleIndex}`} divider={ruleIndex < (policy.rules.length - 1)}> <ListItemText primary={rule.name || 'Unnamed Rule'} secondary={rule.message || 'No message provided.'} primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', mb: 0.2 }} secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }} /> </ListItem> )) ) : ( <ListItem><ListItemText primary="No specific rules listed for this policy." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.8rem' }} /></ListItem> )}
//                                                                                                  </List>
//                                                                                              </AccordionDetails>
//                                                                                          </Accordion>
//                                                                                      );
//                                                                                  })
//                                                                             ) : ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem', mt: 2, mb: 1 }}> No policies found in this result set. </Typography> )}
//                                                                         </Box>
//                                                                     </Collapse>
//                                                                 </React.Fragment>
//                                                             );
//                                                         })}
//                                                     </List>
//                                                 </Box>
//                                             </>
//                                         );
//                                     })()}
//                                 </Paper>
//                             )}

//                             {/* Case 2: Multiple resource types exist -> Render Accordion for each type (Fallback/Future Use)
//                             {/* This part of the logic is less relevant now but kept for potential future needs */}
                             
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default KyvernoDetails;

// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     // Divider,
//     Grid,
//     Avatar,
//     Stack,
//     useTheme // Import useTheme to access theme colors
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For Validation Action tooltip
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Placeholder for Kyverno Logo ---
// const KYVERNO_LOGO_PATH = "/assets/kyverno.svg"; // UPDATE THIS with your actual path

// // --- Kyverno Component ---
// const KyvernoDetails: React.FC = () => {
//     const navigate = useNavigate();
//     const theme = useTheme(); // Access theme for potential color overrides

//     // --- State Variables ---
//     const [allKyvernoData, setAllKyvernoData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({}); // Grouped by resourceType ('cluster')
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false); // For 'cluster' accordion
//     const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null); // For individual cluster result
//     const [expandedPolicyKey, setExpandedPolicyKey] = useState<string | null>(null); // For individual policy within a cluster result (e.g., "clusterId-policyName")

//     // --- GraphQL Query ---
//     const getKyvernoResultsQuery = `
//         query GetKyvernoResults {
//           getKyvernoResults {
//             id
//             resourceId
//             resourceType
//             policies {
//               category
//               description
//               name
//               rules {
//                 message
//                 name
//               }
//               severity
//               title
//               validationFailureAction
//             }
//             cluster {
//               clusterName
//             }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllKyvernoData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//             try {
//                 const response = await queryGraphql(getKyvernoResultsQuery);
//                 const fetchedData = response?.getKyvernoResults || [];
//                 if (Array.isArray(fetchedData)) {
//                     if (fetchedData.length === 0) {
//                         setAllKyvernoData([]);
//                         setGroupedData({});
//                     } else {
//                         setAllKyvernoData(fetchedData);
//                         // Group by resourceType (will likely only be 'cluster')
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort by update time within the group (most recent first)
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     // Prioritize parsing as number (timestamp)
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || 0); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch { }
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || 0); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch { }
//                                      // Fallback for string dates if number parsing failed or wasn't applicable
//                                      if (timeA === 0 && typeof tsA === 'string') { try { const dateA = new Date(tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}}
//                                      if (timeB === 0 && typeof tsB === 'string') { try { const dateB = new Date(tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}}

//                                     return timeB - timeA; // Descending
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                         // If only clusters exist, pre-expand that section
//                         if (Object.keys(groups).length === 1 && groups['cluster']) {
//                              setExpandedAccordion('cluster');
//                         }
//                     }
//                 } else if (response && response.errors) {
//                     setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 } else {
//                     setError("Could not fetch Kyverno data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 setError(err.message || "An unexpected error occurred.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Dependencies: Ensure queryGraphql doesn't change unexpectedly or add it if needed

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         // Collapse nested levels when the main accordion changes
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handleClusterSelect = (clusterResultId: string | undefined | null) => {
//         if (!clusterResultId) return;
//         const newClusterId = expandedClusterId === clusterResultId ? null : clusterResultId;
//         setExpandedClusterId(newClusterId);
//         // Collapse policy level if cluster selection changes
//         if (expandedClusterId !== newClusterId) {
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handlePolicyToggle = (policyKey: string | undefined | null) => {
//          if (!policyKey) return;
//          setExpandedPolicyKey(prevKey => (prevKey === policyKey ? null : policyKey));
//     };

//     // --- Helper Functions ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//             if (typeof timestamp === 'number') { date = new Date(timestamp); }
//             else if (typeof timestamp === 'string') {
//                 const nTs = Number(timestamp);
//                 // Check if it's a likely timestamp (e.g., more than 5 digits) before converting number
//                  if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { // >= 10 for seconds/ms timestamps
//                      date = new Date(nTs);
//                  } else {
//                      date = new Date(timestamp); // Try parsing as string date
//                  }
//             }
//             if (date && !isNaN(date.getTime())) {
//                 // Use toLocaleDateString for date only
//                 return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
//             } else {
//                 return "Invalid Date";
//             }
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//         try {
//             if (!isNaN(dateObj.getTime())) {
//                 return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//             } else { return "Invalid Date"; }
//         }
//         catch (e) { return "Invalid Date"; }
//     }

//     const getClusterDisplayName = (scan: any): string => {
//         return scan?.cluster?.clusterName ?? scan?.resourceId ?? 'Unknown Cluster';
//     };

//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allKyvernoData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//             if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                           if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { scanDate = new Date(nTs); }
//                           else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) {
//                              latestDateObj = scanDate;
//                          }
//                      }
//                  } catch (e) { /* ignore parsing errors */ }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allKyvernoData]);

//     const getSeverityColor = (severity: string | undefined | null): { color: string, muiColor: 'success' | 'warning' | 'error' | 'info' } => {
//         const sev = severity?.toLowerCase();
//         if (sev === 'high') return { color: '#D9534F', muiColor: 'error' }; // Red
//         if (sev === 'medium') return { color: '#F0AD4E', muiColor: 'warning' }; // Orange/Yellow
//         if (sev === 'low') return { color: '#5CB85C', muiColor: 'success' }; // Green
//         return { color: theme.palette.grey[500], muiColor: 'info' }; // Default grey for unknown/other
//     };

//     const getValidationActionChipStyle = (action: string | undefined | null): React.CSSProperties => {
//         const act = action?.toLowerCase();
//         if (act === 'audit') {
//             return {
//                 backgroundColor: '#FFF8E1', // Light Amber/Yellow
//                 color: theme.palette.warning.dark, // Darker yellow/brown text
//             };
//         }
//         // Use 'enforce' as the likely value based on Kyverno docs
//         if (act === 'enforce') {
//              return {
//                 backgroundColor: '#FFEBEE', // Light Pink/Red
//                 color: theme.palette.error.dark, // Darker red text
//             };
//         }
//         // Default style
//         return {
//             backgroundColor: '#eeeeee', // Light Grey
//             color: theme.palette.text.secondary,
//         };
//     };
//     // --- End Helper Functions ---

//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//             <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Kyverno </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Kyverno </Typography>
//                 </Box>
//             </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30} /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Kyverno Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allKyvernoData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Kyverno policy results are available to display. Ensure Kyverno is running and reporting results. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allKyvernoData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of policies evaluated by Kyverno. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={KYVERNO_LOGO_PATH} alt="Kyverno Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> K </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Kyverno </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Kubernetes native policy management for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Result Received: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types (Likely just 'cluster') */}
//                         <Box>
//                             {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No Kyverno results found after loading. </Typography> )}
//                             {/* Expecting only 'cluster' key based on provided data */}
//                             {(['cluster'] as const).map((resourceType) => {
//                                 const results = groupedData[resourceType]; // These are the individual Kyverno result entries
//                                 if (!Array.isArray(results) || results.length === 0) return null;
//                                 const resourceTypeName = "Clusters"; // Hardcoded as data only shows clusters

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({results.length} result set{results.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Cluster Results */}
//                                             <List dense disablePadding>
//                                                 {results.map((result: any, index: number) => {
//                                                     const isClusterExpanded = expandedClusterId === result?.id;
//                                                     const policyCount = result?.policies?.length ?? 0;
//                                                     const clusterName = getClusterDisplayName(result);
//                                                     const resultDate = formatDate(result?.updatedAt);

//                                                     return (
//                                                         <React.Fragment key={result?.id ?? `result-${index}`}>
//                                                             {/* Clickable Item for each Cluster Result */}
//                                                             <ListItemButton
//                                                                 selected={isClusterExpanded}
//                                                                 onClick={() => handleClusterSelect(result?.id)}
//                                                                 sx={{
//                                                                     '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } },
//                                                                     px: { xs: 2, md: 3 }, py: 1.5, // Adjusted padding
//                                                                     borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none'
//                                                                 }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={clusterName}
//                                                                     secondary={`Result Date: ${resultDate}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isClusterExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 <Chip
//                                                                     label={`${policyCount} Policies`}
//                                                                     size="small"
//                                                                     sx={{ ml: 2, fontFamily:'Gilroy-Regular', height: '24px', bgcolor: '#e0e0e0', color: '#333' }}
//                                                                 />
//                                                             </ListItemButton>

//                                                             {/* Collapsible Detail Section for the selected Cluster Result */}
//                                                             <Collapse in={isClusterExpanded} timeout="auto" unmountOnExit>
//                                                                 <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2.5 }}> Policies Evaluated in: {clusterName} </Typography>

//                                                                     {/* Nested Accordions for Policies */}
//                                                                     {(result?.policies?.length ?? 0) > 0 ? (
//                                                                         result.policies.map((policy: any, policyIndex: number) => {
//                                                                             const policyKey = `${result.id}-${policy.name}`; // Unique key for expansion state
//                                                                             const isPolicyExpanded = expandedPolicyKey === policyKey;
//                                                                             const severityInfo = getSeverityColor(policy.severity);
//                                                                             const validationActionStyle = getValidationActionChipStyle(policy.validationFailureAction);
//                                                                             // --- CHANGE: Calculate rule count ---
//                                                                             const ruleCount = policy?.rules?.length ?? 0;

//                                                                             return (
//                                                                                 <Accordion
//                                                                                     key={policyKey}
//                                                                                     expanded={isPolicyExpanded}
//                                                                                     onChange={() => handlePolicyToggle(policyKey)}
//                                                                                     disableGutters
//                                                                                     elevation={0}
//                                                                                     sx={{
//                                                                                         border: '1px solid #e0e0e0',
//                                                                                         borderRadius: '6px !important',
//                                                                                         boxShadow: 'none',
//                                                                                         '&:before': { display: 'none' },
//                                                                                         mb: 1.5,
//                                                                                         overflow: 'hidden',
//                                                                                          bgcolor: isPolicyExpanded ? '#f9f9f9' : 'transparent'
//                                                                                     }}
//                                                                                 >
//                                                                                     <AccordionSummary
//                                                                                         expandIcon={<ExpandMoreIcon />}
//                                                                                         aria-controls={`${policyKey}-content`}
//                                                                                         id={`${policyKey}-header`}
//                                                                                         sx={{ px: 2, py: 0.5, minHeight: '48px', '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 } }}
//                                                                                     >
//                                                                                          {/* Policy Name and Title */}
//                                                                                         <Box sx={{ flexGrow: 1, mr: 1 }}>
//                                                                                             {/* --- CHANGE: Added rule count --- */}
//                                                                                             <Typography fontFamily="Gilroy-Medium" fontSize="0.9rem" component="div">
//                                                                                                 {policy.name}
//                                                                                                 <Typography component="span" sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', ml: 0.75 }}>
//                                                                                                     ({ruleCount} Rule{ruleCount !== 1 ? 's' : ''})
//                                                                                                 </Typography>
//                                                                                             </Typography>
//                                                                                             <Typography fontFamily="Gilroy-Regular" fontSize="0.8rem" color="text.secondary" component="div">{policy.title}</Typography>
//                                                                                         </Box>
//                                                                                         {/* Severity and Action Chips */}
//                                                                                         <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
//                                                                                              <Tooltip title={`Severity: ${policy.severity?.toUpperCase() ?? 'N/A'}`} placement="top">
//                                                                                                 <Chip
//                                                                                                     label={policy.severity?.toUpperCase() ?? 'N/A'}
//                                                                                                     size="small"
//                                                                                                     sx={{
//                                                                                                          bgcolor: severityInfo.color,
//                                                                                                          color: '#fff',
//                                                                                                          height: '28px',
//                                                                                                          fontSize: '0.8rem',
//                                                                                                          fontFamily: 'Gilroy-SemiBold',
//                                                                                                          borderRadius: '16px',
//                                                                                                          '& .MuiChip-label': { pl: '12px', pr: '12px' }
//                                                                                                     }}
//                                                                                                 />
//                                                                                              </Tooltip>
//                                                                                              <Tooltip title={`Action on Failure: ${policy.validationFailureAction ?? 'N/A'}`} placement="top">
//                                                                                                  <Chip
//                                                                                                     label={policy.validationFailureAction ?? 'N/A'}
//                                                                                                     icon={<InfoOutlinedIcon sx={{ fontSize: '1rem', ml: '6px', mr: '-4px' }} />}
//                                                                                                     size="small"
//                                                                                                     style={validationActionStyle} // Use style prop for CSS properties
//                                                                                                     sx={{
//                                                                                                         height: '22px',
//                                                                                                         fontSize: '0.75rem',
//                                                                                                         fontFamily: 'Gilroy-Regular',
//                                                                                                         border: 'none',
//                                                                                                         '& .MuiChip-icon': { color: validationActionStyle.color }
//                                                                                                     }}
//                                                                                                 />
//                                                                                              </Tooltip>
//                                                                                         </Stack>
//                                                                                     </AccordionSummary>
//                                                                                     <AccordionDetails sx={{ px: 2, pt: 1, pb: 2, borderTop: '1px solid #eee' }}>
//                                                                                          {/* Policy Description */}
//                                                                                         <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', color: 'text.secondary', mb: 2, whiteSpace: 'pre-wrap' }}>
//                                                                                              {policy.description || 'No description provided.'}
//                                                                                         </Typography>

//                                                                                          {/* Rules List */}
//                                                                                         <Typography variant="subtitle2" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.85rem', mb: 1 }}> Rules: </Typography>
//                                                                                         <List dense disablePadding sx={{ border: '1px solid #eee', borderRadius: '4px', bgcolor: '#fff' }}>
//                                                                                             {(policy?.rules?.length ?? 0) > 0 ? (
//                                                                                                 policy.rules.map((rule: any, ruleIndex: number) => (
//                                                                                                     <ListItem key={`${policyKey}-rule-${rule.name ?? ruleIndex}`} divider={ruleIndex < (policy.rules.length - 1)}>
//                                                                                                         <ListItemText
//                                                                                                             primary={rule.name || 'Unnamed Rule'}
//                                                                                                             secondary={rule.message || 'No message provided.'}
//                                                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', mb: 0.2 }}
//                                                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                                                         />
//                                                                                                     </ListItem>
//                                                                                                 ))
//                                                                                             ) : ( <ListItem><ListItemText primary="No specific rules listed for this policy." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.8rem' }} /></ListItem> )}
//                                                                                         </List>
//                                                                                     </AccordionDetails>
//                                                                                 </Accordion>
//                                                                             );
//                                                                         })
//                                                                     ) : (
//                                                                         <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem', mt: 2, mb: 1 }}> No policies found in this result set. </Typography>
//                                                                     )}
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default KyvernoDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     // Divider, // Divider might not be needed if using borders consistently
//     Grid,
//     Avatar,
//     Stack,
//     useTheme // Import useTheme to access theme colors
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For Validation Action tooltip
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Placeholder for Kyverno Logo ---
// const KYVERNO_LOGO_PATH = "/assets/kyverno.svg"; // UPDATE THIS with your actual path

// // --- Kyverno Component ---
// const KyvernoDetails: React.FC = () => {
//     const navigate = useNavigate();
//     const theme = useTheme(); // Access theme for potential color overrides

//     // --- State Variables ---
//     const [allKyvernoData, setAllKyvernoData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({}); // Grouped by resourceType ('cluster')
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false); // For 'cluster' accordion
//     const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null); // For individual cluster result
//     const [expandedPolicyKey, setExpandedPolicyKey] = useState<string | null>(null); // For individual policy within a cluster result (e.g., "clusterId-policyName")

//     // --- GraphQL Query ---
//     const getKyvernoResultsQuery = `
//         query GetKyvernoResults {
//           getKyvernoResults {
//             id
//             resourceId
//             resourceType
//             policies {
//               category
//               description
//               name
//               rules {
//                 message
//                 name
//               }
//               severity
//               title
//               validationFailureAction
//             }
//             cluster {
//               clusterName
//             }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllKyvernoData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//             try {
//                 const response = await queryGraphql(getKyvernoResultsQuery);
//                 const fetchedData = response?.getKyvernoResults || [];
//                 if (Array.isArray(fetchedData)) {
//                     if (fetchedData.length === 0) {
//                         setAllKyvernoData([]);
//                         setGroupedData({});
//                     } else {
//                         setAllKyvernoData(fetchedData);
//                         // Group by resourceType (will likely only be 'cluster')
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort by update time within the group (most recent first)
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     // Prioritize parsing as number (timestamp)
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || 0); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch { }
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || 0); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch { }
//                                      // Fallback for string dates if number parsing failed or wasn't applicable
//                                      if (timeA === 0 && typeof tsA === 'string') { try { const dateA = new Date(tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch {}}
//                                      if (timeB === 0 && typeof tsB === 'string') { try { const dateB = new Date(tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch {}}

//                                     return timeB - timeA; // Descending
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                         // If only clusters exist, pre-expand that section
//                         if (Object.keys(groups).length === 1 && groups['cluster']) {
//                              setExpandedAccordion('cluster');
//                         }
//                     }
//                 } else if (response && response.errors) {
//                     setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 } else {
//                     setError("Could not fetch Kyverno data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 setError(err.message || "An unexpected error occurred.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Dependencies: Ensure queryGraphql doesn't change unexpectedly or add it if needed

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         // Collapse nested levels when the main accordion changes
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handleClusterSelect = (clusterResultId: string | undefined | null) => {
//         if (!clusterResultId) return;
//         const newClusterId = expandedClusterId === clusterResultId ? null : clusterResultId;
//         setExpandedClusterId(newClusterId);
//         // Collapse policy level if cluster selection changes
//         if (expandedClusterId !== newClusterId) {
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handlePolicyToggle = (policyKey: string | undefined | null) => {
//          if (!policyKey) return;
//          setExpandedPolicyKey(prevKey => (prevKey === policyKey ? null : policyKey));
//     };

//     // --- Helper Functions ---
//     // CHANGE: Updated formatDate to return only the date string
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//             if (typeof timestamp === 'number') { date = new Date(timestamp); }
//             else if (typeof timestamp === 'string') {
//                 const nTs = Number(timestamp);
//                 // Check if it's a likely timestamp (e.g., more than 5 digits) before converting number
//                  if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { // >= 10 for seconds/ms timestamps
//                      date = new Date(nTs);
//                  } else {
//                      date = new Date(timestamp); // Try parsing as string date
//                  }
//             }
//             if (date && !isNaN(date.getTime())) {
//                 // Use toLocaleDateString for date only
//                 return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
//             } else {
//                 return "Invalid Date";
//             }
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//     // formatHeaderDate already returns only the date, no change needed here
//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//         try {
//             if (!isNaN(dateObj.getTime())) {
//                 return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//             } else { return "Invalid Date"; }
//         }
//         catch (e) { return "Invalid Date"; }
//     }

//     const getClusterDisplayName = (scan: any): string => {
//         return scan?.cluster?.clusterName ?? scan?.resourceId ?? 'Unknown Cluster';
//     };

//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allKyvernoData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//             if (timestamp !== null && timestamp !== undefined) {
//                  try {
//                      let scanDate: Date | null = null;
//                      if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                      else if (typeof timestamp === 'string') {
//                          const nTs = Number(timestamp);
//                           if (!isNaN(nTs) && timestamp.length >= 10 && String(nTs) === timestamp) { scanDate = new Date(nTs); }
//                           else { scanDate = new Date(timestamp); }
//                      }
//                      if (scanDate && !isNaN(scanDate.getTime())) {
//                          if (!latestDateObj || scanDate > latestDateObj) {
//                              latestDateObj = scanDate;
//                          }
//                      }
//                  } catch (e) { /* ignore parsing errors */ }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allKyvernoData]);

//     const getSeverityColor = (severity: string | undefined | null): { color: string, muiColor: 'success' | 'warning' | 'error' | 'info' } => {
//         const sev = severity?.toLowerCase();
//         if (sev === 'high') return { color: '#D9534F', muiColor: 'error' }; // Red
//         if (sev === 'medium') return { color: '#F0AD4E', muiColor: 'warning' }; // Orange/Yellow
//         if (sev === 'low') return { color: '#5CB85C', muiColor: 'success' }; // Green
//         return { color: theme.palette.grey[500], muiColor: 'info' }; // Default grey for unknown/other
//     };

//     // CHANGE: Added helper for Validation Action chip styling
//     const getValidationActionChipStyle = (action: string | undefined | null): React.CSSProperties => {
//         const act = action?.toLowerCase();
//         if (act === 'audit') {
//             return {
//                 backgroundColor: '#FFF8E1', // Light Amber/Yellow
//                 color: theme.palette.warning.dark, // Darker yellow/brown text
//             };
//         }
//         if (act === 'enforced') { // Assuming 'Enforce' might be the value
//              return {
//                 backgroundColor: '#FFEBEE', // Light Pink/Red
//                 color: theme.palette.error.dark, // Darker red text
//             };
//         }
//         // Default style
//         return {
//             backgroundColor: '#eeeeee', // Light Grey
//             color: theme.palette.text.secondary,
//         };
//     };
//     // --- End Helper Functions ---

//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//             <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Kyverno </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Kyverno </Typography>
//                 </Box>
//             </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30} /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Kyverno Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allKyvernoData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Kyverno policy results are available to display. Ensure Kyverno is running and reporting results. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allKyvernoData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of policies evaluated by Kyverno. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={KYVERNO_LOGO_PATH} alt="Kyverno Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> K </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Kyverno </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Kubernetes native policy management for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Result Received: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types (Likely just 'cluster') */}
//                         <Box>
//                             {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No Kyverno results found after loading. </Typography> )}
//                             {/* Expecting only 'cluster' key based on provided data */}
//                             {(['cluster'] as const).map((resourceType) => {
//                                 const results = groupedData[resourceType]; // These are the individual Kyverno result entries
//                                 if (!Array.isArray(results) || results.length === 0) return null;
//                                 const resourceTypeName = "Clusters"; // Hardcoded as data only shows clusters

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({results.length} result set{results.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Cluster Results */}
//                                             <List dense disablePadding>
//                                                 {results.map((result: any, index: number) => {
//                                                     const isClusterExpanded = expandedClusterId === result?.id;
//                                                     const policyCount = result?.policies?.length ?? 0;
//                                                     const clusterName = getClusterDisplayName(result);
//                                                     // CHANGE: Use updated formatDate here
//                                                     const resultDate = formatDate(result?.updatedAt);

//                                                     return (
//                                                         <React.Fragment key={result?.id ?? `result-${index}`}>
//                                                             {/* Clickable Item for each Cluster Result */}
//                                                             <ListItemButton
//                                                                 selected={isClusterExpanded}
//                                                                 onClick={() => handleClusterSelect(result?.id)}
//                                                                 sx={{
//                                                                     '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } },
//                                                                     px: { xs: 2, md: 3 }, py: 1.5, // Adjusted padding
//                                                                     borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none'
//                                                                 }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={clusterName}
//                                                                     // CHANGE: Display only date in secondary text
//                                                                     secondary={`Result Date: ${resultDate}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isClusterExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 <Chip
//                                                                     label={`${policyCount} Policies`}
//                                                                     size="small"
//                                                                     sx={{ ml: 2, fontFamily:'Gilroy-Regular', height: '24px', bgcolor: '#e0e0e0', color: '#333' }}
//                                                                 />
//                                                             </ListItemButton>

//                                                             {/* Collapsible Detail Section for the selected Cluster Result */}
//                                                             <Collapse in={isClusterExpanded} timeout="auto" unmountOnExit>
//                                                                  {/* CHANGE: Removed date from this title */}
//                                                                 <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2.5 }}> Policies Evaluated in: {clusterName} </Typography>

//                                                                     {/* Nested Accordions for Policies */}
//                                                                     {(result?.policies?.length ?? 0) > 0 ? (
//                                                                         result.policies.map((policy: any, policyIndex: number) => {
//                                                                             const policyKey = `${result.id}-${policy.name}`; // Unique key for expansion state
//                                                                             const isPolicyExpanded = expandedPolicyKey === policyKey;
//                                                                             const severityInfo = getSeverityColor(policy.severity);
//                                                                             // CHANGE: Get style for validation action chip
//                                                                             const validationActionStyle = getValidationActionChipStyle(policy.validationFailureAction);

//                                                                             return (
//                                                                                 <Accordion
//                                                                                     key={policyKey}
//                                                                                     expanded={isPolicyExpanded}
//                                                                                     onChange={() => handlePolicyToggle(policyKey)}
//                                                                                     disableGutters
//                                                                                     elevation={0}
//                                                                                     sx={{
//                                                                                         border: '1px solid #e0e0e0',
//                                                                                         borderRadius: '6px !important', // Ensure radius applies correctly
//                                                                                         boxShadow: 'none',
//                                                                                         '&:before': { display: 'none' },
//                                                                                         mb: 1.5,
//                                                                                         overflow: 'hidden', // Prevent content spill on radius
//                                                                                          bgcolor: isPolicyExpanded ? '#f9f9f9' : 'transparent' // Slight background change when expanded
//                                                                                     }}
//                                                                                 >
//                                                                                     <AccordionSummary
//                                                                                         expandIcon={<ExpandMoreIcon />} // Policy accordion retains arrow
//                                                                                         aria-controls={`${policyKey}-content`}
//                                                                                         id={`${policyKey}-header`}
//                                                                                         sx={{ px: 2, py: 0.5, minHeight: '48px', '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 } }}
//                                                                                     >
//                                                                                          {/* Policy Name and Title */}
//                                                                                         <Box sx={{ flexGrow: 1, mr: 1 }}>
//                                                                                             <Typography fontFamily="Gilroy-Medium" fontSize="0.9rem" component="div">{policy.name}</Typography>
//                                                                                             <Typography fontFamily="Gilroy-Regular" fontSize="0.8rem" color="text.secondary" component="div">{policy.title}</Typography>
//                                                                                         </Box>
//                                                                                         {/* Severity and Action Chips */}
//                                                                                         <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
//                                                                                              <Tooltip title={`Severity: ${policy.severity?.toUpperCase() ?? 'N/A'}`} placement="top">
//                                                                                                 <Chip
//                                                                                                     label={policy.severity?.toUpperCase() ?? 'N/A'}
//                                                                                                     size="small"
//                                                                                                     sx={{
//                                                                                                         bgcolor: severityInfo.color,
//                                                                                                         color: '#fff', // White text for better contrast on colored bg
//                                                                                                         height: '28px', // Maintain fixed height
//                                                                                                         fontSize: '0.8rem',
//                                                                                                         fontFamily: 'Gilroy-SemiBold',
//                                                                                                         borderRadius: '16px', // Maintain rounded corners
//                                                                                                         // Only apply horizontal padding to the label if needed, remove vertical
//                                                                                                         '& .MuiChip-label': { pl: '12px', pr: '12px' } // Adjust pl/pr as desired
//                                                                                                     }}
//                                                                                                 />
//                                                                                              </Tooltip>
//                                                                                              <Tooltip title={`Action on Failure: ${policy.validationFailureAction ?? 'N/A'}`} placement="top">
//                                                                                                   {/* CHANGE: Updated style for validation action chip */}
//                                                                                                  <Chip
//                                                                                                     label={policy.validationFailureAction ?? 'N/A'}
//                                                                                                     icon={<InfoOutlinedIcon sx={{ fontSize: '1rem', ml: '6px', mr: '-4px' }} />} // Adjusted icon margin
//                                                                                                     size="small"
//                                                                                                     // variant="filled" // Default variant
//                                                                                                     sx={{
//                                                                                                         height: '22px',
//                                                                                                         fontSize: '0.75rem',
//                                                                                                         fontFamily: 'Gilroy-Regular',
//                                                                                                         border: 'none', // Remove border if previously outlined
//                                                                                                         ...validationActionStyle, // Apply dynamic background/color
//                                                                                                         '& .MuiChip-icon': { color: validationActionStyle.color } // Make icon color match text
//                                                                                                     }}
//                                                                                                 />
//                                                                                              </Tooltip>
//                                                                                         </Stack>
//                                                                                     </AccordionSummary>
//                                                                                     <AccordionDetails sx={{ px: 2, pt: 1, pb: 2, borderTop: '1px solid #eee' }}>
//                                                                                          {/* Policy Description */}
//                                                                                         <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', color: 'text.secondary', mb: 2, whiteSpace: 'pre-wrap' }}>
//                                                                                              {policy.description || 'No description provided.'}
//                                                                                         </Typography>

//                                                                                          {/* Rules List */}
//                                                                                         <Typography variant="subtitle2" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.85rem', mb: 1 }}> Rules: </Typography>
//                                                                                         <List dense disablePadding sx={{ border: '1px solid #eee', borderRadius: '4px', bgcolor: '#fff' }}>
//                                                                                             {(policy?.rules?.length ?? 0) > 0 ? (
//                                                                                                 policy.rules.map((rule: any, ruleIndex: number) => (
//                                                                                                     <ListItem key={`${policyKey}-rule-${rule.name ?? ruleIndex}`} divider={ruleIndex < (policy.rules.length - 1)}>
//                                                                                                         <ListItemText
//                                                                                                             primary={rule.name || 'Unnamed Rule'}
//                                                                                                             secondary={rule.message || 'No message provided.'}
//                                                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', mb: 0.2 }}
//                                                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                                                         />
//                                                                                                     </ListItem>
//                                                                                                 ))
//                                                                                             ) : ( <ListItem><ListItemText primary="No specific rules listed for this policy." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.8rem' }} /></ListItem> )}
//                                                                                         </List>
//                                                                                     </AccordionDetails>
//                                                                                 </Accordion>
//                                                                             );
//                                                                         })
//                                                                     ) : (
//                                                                         <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem', mt: 2, mb: 1 }}> No policies found in this result set. </Typography>
//                                                                     )}
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default KyvernoDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState, useEffect, useMemo } from "react";
// import {
//     Box,
//     Breadcrumbs,
//     Chip,
//     CircularProgress,
//     Collapse,
//     Container,
//     IconButton,
//     Link,
//     List,
//     ListItem,
//     ListItemButton,
//     ListItemText,
//     Paper,
//     Typography,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     Tooltip,
//     Divider,
//     Grid,
//     Avatar,
//     Stack,
//     useTheme // Import useTheme to access theme colors
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // For Validation Action tooltip
// import queryGraphql from "../../helpers/ApiInterface"; // Assuming this path is correct

// // --- Placeholder for Kyverno Logo ---
// const KYVERNO_LOGO_PATH = "/assets/kyverno.svg"; // UPDATE THIS with your actual path

// // --- Kyverno Component ---
// const KyvernoDetails: React.FC = () => {
//     const navigate = useNavigate();
//     const theme = useTheme(); // Access theme for potential color overrides

//     // --- State Variables ---
//     const [allKyvernoData, setAllKyvernoData] = useState<any[]>([]);
//     const [groupedData, setGroupedData] = useState<any>({}); // Grouped by resourceType ('cluster')
//     const [isLoading, setIsLoading] = useState<boolean>(true);
//     const [error, setError] = useState<string | null>(null);
//     const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false); // For 'cluster' accordion
//     const [expandedClusterId, setExpandedClusterId] = useState<string | null>(null); // For individual cluster result
//     const [expandedPolicyKey, setExpandedPolicyKey] = useState<string | null>(null); // For individual policy within a cluster result (e.g., "clusterId-policyName")

//     // --- GraphQL Query ---
//     const getKyvernoResultsQuery = `
//         query GetKyvernoResults {
//           getKyvernoResults {
//             id
//             resourceId
//             resourceType
//             policies {
//               category
//               description
//               name
//               rules {
//                 message
//                 name
//               }
//               severity
//               title
//               validationFailureAction
//             }
//             cluster {
//               clusterName
//             }
//             createdAt
//             updatedAt
//           }
//         }
//     `;

//     // --- Data Fetching and Grouping Effect ---
//     useEffect(() => {
//         const fetchData = async () => {
//             setIsLoading(true);
//             setError(null);
//             setAllKyvernoData([]);
//             setGroupedData({});
//             setExpandedAccordion(false);
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//             try {
//                 const response = await queryGraphql(getKyvernoResultsQuery);
//                 const fetchedData = response?.getKyvernoResults || [];
//                 if (Array.isArray(fetchedData)) {
//                     if (fetchedData.length === 0) {
//                         setAllKyvernoData([]);
//                         setGroupedData({});
//                     } else {
//                         setAllKyvernoData(fetchedData);
//                         // Group by resourceType (will likely only be 'cluster')
//                         const groups = fetchedData.reduce((acc, result) => {
//                             const type = result?.resourceType;
//                             if (type && result) {
//                                 if (!acc[type]) { acc[type] = []; }
//                                 acc[type]!.push(result);
//                                 // Sort by update time within the group (most recent first)
//                                 acc[type]?.sort((a: any, b: any) => {
//                                     let timeA = 0, timeB = 0;
//                                     const tsA = a?.updatedAt, tsB = b?.updatedAt;
//                                     try { const dateA = new Date(typeof tsA === 'number' ? tsA : Number(tsA) || tsA); if (!isNaN(dateA.getTime())) timeA = dateA.getTime(); } catch { }
//                                     try { const dateB = new Date(typeof tsB === 'number' ? tsB : Number(tsB) || tsB); if (!isNaN(dateB.getTime())) timeB = dateB.getTime(); } catch { }
//                                     return timeB - timeA; // Descending
//                                 });
//                             }
//                             return acc;
//                         }, {} as any);
//                         setGroupedData(groups);
//                         // If only clusters exist, pre-expand that section
//                         if (Object.keys(groups).length === 1 && groups['cluster']) {
//                              setExpandedAccordion('cluster');
//                         }
//                     }
//                 } else if (response && response.errors) {
//                     setError(`GraphQL Error: ${response.errors[0]?.message || 'Unknown GraphQL error'}`);
//                 } else {
//                     setError("Could not fetch Kyverno data or the format is incorrect.");
//                 }
//             } catch (err: any) {
//                 setError(err.message || "An unexpected error occurred.");
//             } finally {
//                 setIsLoading(false);
//             }
//         };
//         fetchData();
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, []); // Dependencies: Ensure queryGraphql doesn't change unexpectedly or add it if needed

//     // --- Event Handlers ---
//     const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
//         setExpandedAccordion(isExpanded ? panel : false);
//         // Collapse nested levels when the main accordion changes
//         if (!isExpanded || (isExpanded && panel !== expandedAccordion)) {
//             setExpandedClusterId(null);
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handleClusterSelect = (clusterResultId: string | undefined | null) => {
//         if (!clusterResultId) return;
//         const newClusterId = expandedClusterId === clusterResultId ? null : clusterResultId;
//         setExpandedClusterId(newClusterId);
//         // Collapse policy level if cluster selection changes
//         if (expandedClusterId !== newClusterId) {
//             setExpandedPolicyKey(null);
//         }
//     };

//     const handlePolicyToggle = (policyKey: string | undefined | null) => {
//          if (!policyKey) return;
//          setExpandedPolicyKey(prevKey => (prevKey === policyKey ? null : policyKey));
//     };

//     // --- Helper Functions ---
//     const formatDate = (timestamp: string | number | null | undefined): string => {
//         if (timestamp === null || timestamp === undefined) return "N/A";
//         let date: Date | null = null;
//         try {
//             if (typeof timestamp === 'number') { date = new Date(timestamp); }
//             else if (typeof timestamp === 'string') {
//                 const nTs = Number(timestamp);
//                 // Check if it's a likely timestamp (e.g., more than 5 digits) before converting number
//                 if (!isNaN(nTs) && timestamp.length > 5 && String(nTs) === timestamp) { date = new Date(nTs); }
//                 else { date = new Date(timestamp); } // Try parsing as string date
//             }
//             if (date && !isNaN(date.getTime())) {
//                 return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
//             } else {
//                 return "Invalid Date";
//             }
//         } catch (e) {
//             return "Invalid Date";
//         }
//     };

//     const formatHeaderDate = (dateObj: Date | null): string => {
//         if (!dateObj) return "N/A";
//         try {
//             if (!isNaN(dateObj.getTime())) {
//                 return dateObj.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });
//             } else { return "Invalid Date"; }
//         }
//         catch (e) { return "Invalid Date"; }
//     }

//     const getClusterDisplayName = (scan: any): string => {
//         return scan?.cluster?.clusterName ?? scan?.resourceId ?? 'Unknown Cluster';
//     };

//     const { overallLatestScanDate } = useMemo(() => {
//         let latestDateObj: Date | null = null;
//         allKyvernoData.forEach(scan => {
//             const timestamp = scan?.updatedAt;
//             if (timestamp !== null && timestamp !== undefined) {
//                 try {
//                     let scanDate: Date | null = null;
//                     if (typeof timestamp === 'number') { scanDate = new Date(timestamp); }
//                     else if (typeof timestamp === 'string') {
//                         const nTs = Number(timestamp);
//                          if (!isNaN(nTs) && timestamp.length > 5 && String(nTs) === timestamp) { scanDate = new Date(nTs); }
//                          else { scanDate = new Date(timestamp); }
//                     }
//                     if (scanDate && !isNaN(scanDate.getTime())) {
//                         if (!latestDateObj || scanDate > latestDateObj) {
//                             latestDateObj = scanDate;
//                         }
//                     }
//                 } catch (e) { /* ignore parsing errors */ }
//             }
//         });
//         return { overallLatestScanDate: latestDateObj };
//     }, [allKyvernoData]);

//     const getSeverityColor = (severity: string | undefined | null): { color: string, muiColor: 'success' | 'warning' | 'error' | 'info' } => {
//         const sev = severity?.toLowerCase();
//         if (sev === 'high') return { color: '#D9534F', muiColor: 'error' }; // Red
//         if (sev === 'medium') return { color: '#F0AD4E', muiColor: 'warning' }; // Orange/Yellow
//         if (sev === 'low') return { color: '#5CB85C', muiColor: 'success' }; // Green
//         return { color: theme.palette.grey[500], muiColor: 'info' }; // Default grey for unknown/other
//     };
//     // --- End Helper Functions ---

//     // --- Render Logic ---
//     return (
//         <Container disableGutters maxWidth={false} sx={{ padding: { xs: 2, md: 3 }, bgcolor: "#fafafa", minHeight: "100vh" }} >
//             {/* Header */}
//             <Box sx={{ mb: 3 }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray", fontFamily: 'Gilroy-Regular', mb: 1.5 }}>
//                     <Link underline="hover" color="inherit" onClick={(e) => { e.preventDefault(); navigate("/integrations/integrations"); }} sx={{ cursor: 'pointer' }}> Integrations </Link>
//                     <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontFamily: 'Gilroy-Regular' }}> Kyverno </Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" >
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/integrations/integrations")} sx={{ mr: 1, p: 0.5 }} > <ArrowBackIosNewIcon fontSize="small" /> </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="20px"> Kyverno </Typography>
//                 </Box>
//             </Box>

//             {/* Content Area */}
//             <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
//                 {/* Loading/Error/Empty States */}
//                 {isLoading && ( <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: "1px solid #E0E0E0", borderRadius: "12px" }}><CircularProgress size={30} /><Typography sx={{ mt: 2, fontFamily: 'Gilroy-Regular' }}>Loading Kyverno Data...</Typography></Paper> )}
//                 {!isLoading && error && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 3, border: "1px solid", borderColor: 'error.main', textAlign: 'center' }}><Typography color="error" sx={{ fontFamily: 'Gilroy-SemiBold' }}> Error: {error} </Typography></Paper> )}
//                 {!isLoading && !error && allKyvernoData.length === 0 && ( <Paper elevation={1} sx={{ borderRadius: "12px", p: 4, border: "1px solid #E0E0E0", textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> No Kyverno policy results are available to display. Ensure Kyverno is running and reporting results. </Typography> </Paper> )}

//                 {/* Content Display */}
//                 {!isLoading && !error && allKyvernoData.length > 0 && (
//                     <>
//                         {/* Header Card */}
//                         <Typography variant="body1" sx={{ fontFamily: 'Gilroy-Regular', color: 'text.secondary', mb: 1.5, pl: { xs: 1, md: 0 } }}> Review the detailed results of policies evaluated by Kyverno. </Typography>
//                         <Paper elevation={2} sx={{ borderRadius: "12px", p: 2, mb: 3, overflow: 'hidden' }}>
//                             <Grid container spacing={2} alignItems="center">
//                                 <Grid item xs={12} sm="auto"> <Avatar src={KYVERNO_LOGO_PATH} alt="Kyverno Logo" sx={{ width: 50, height: 50, bgcolor: '#eeeeee' }} variant="circular"> K </Avatar> </Grid>
//                                 <Grid item xs={12} sm>
//                                     <Box display="flex" alignItems="center" mb={0.5} >
//                                         <Typography component="div" fontFamily="Gilroy-Bold" fontSize="1.1rem"> Kyverno </Typography>
//                                         <Chip label="POLICIES" size="small" sx={{ ml: 1.5, height: '20px', lineHeight: '1', borderRadius: '4px', bgcolor: '#f5f5f5', color: '#616161', fontFamily: 'Gilroy-Regular', fontWeight: 400, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '1.25px', '& .MuiChip-label': { px: '10px', py: '4px' } }} />
//                                     </Box>
//                                     <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular', mb: 0 }}> Kubernetes native policy management for security and compliance. </Typography>
//                                 </Grid>
//                                 <Grid item xs={12} md="auto" sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 1, sm: 0 } }}> <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Gilroy-Regular' }}> Last Result Received: {formatHeaderDate(overallLatestScanDate)} </Typography> </Grid>
//                             </Grid>
//                         </Paper>

//                         {/* Accordions for Resource Types (Likely just 'cluster') */}
//                         <Box>
//                             {Object.keys(groupedData).length === 0 && !isLoading && !error && ( <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontFamily: 'Gilroy-Regular', mt: 4 }}> No Kyverno results found after loading. </Typography> )}
//                             {/* Expecting only 'cluster' key based on provided data */}
//                             {(['cluster'] as const).map((resourceType) => {
//                                 const results = groupedData[resourceType]; // These are the individual Kyverno result entries
//                                 if (!Array.isArray(results) || results.length === 0) return null;
//                                 const resourceTypeName = "Clusters"; // Hardcoded as data only shows clusters

//                                 return (
//                                     <Accordion key={resourceType} expanded={expandedAccordion === resourceType} onChange={handleAccordionChange(resourceType)} disableGutters elevation={1} sx={{ border: "1px solid #E0E0E0", borderRadius: "8px !important", boxShadow: expandedAccordion === resourceType ? '0 4px 12px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)', '&:before': { display: 'none' }, mb: 2.5, overflow: 'hidden', }} >
//                                         <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${resourceType}-content`} id={`${resourceType}-header`} sx={{ bgcolor: 'background.paper', minHeight: '56px', px: { xs: 2, md: 3 }, py: 1, '& .MuiAccordionSummary-content': { alignItems: 'center' } }}>
//                                             <Typography fontFamily="Gilroy-SemiBold" sx={{ flexShrink: 0, mr: 2 }}> {resourceTypeName} </Typography>
//                                             <Typography sx={{ color: 'text.secondary', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem' }}> ({results.length} result set{results.length !== 1 ? 's' : ''} available) </Typography>
//                                         </AccordionSummary>
//                                         <AccordionDetails sx={{ p: 0, bgcolor: '#fdfdfd' }}>
//                                             {/* List of Cluster Results */}
//                                             <List dense disablePadding>
//                                                 {results.map((result: any, index: number) => {
//                                                     const isClusterExpanded = expandedClusterId === result?.id;
//                                                     const policyCount = result?.policies?.length ?? 0;
//                                                     const clusterName = getClusterDisplayName(result);
//                                                     const resultDate = formatDate(result?.updatedAt);

//                                                     return (
//                                                         <React.Fragment key={result?.id ?? `result-${index}`}>
//                                                             {/* Clickable Item for each Cluster Result */}
//                                                             <ListItemButton
//                                                                 selected={isClusterExpanded}
//                                                                 onClick={() => handleClusterSelect(result?.id)}
//                                                                 sx={{
//                                                                     '&.Mui-selected': { bgcolor: 'action.selected', '&:hover': { bgcolor: 'action.hover' } },
//                                                                     px: { xs: 2, md: 3 }, py: 1.5, // Adjusted padding
//                                                                     borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none'
//                                                                 }}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={clusterName}
//                                                                     secondary={`Result Time: ${resultDate}`}
//                                                                     primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.95rem', color: isClusterExpanded ? 'primary.main' : 'text.primary' }}
//                                                                     secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', mt: 0.3, color: 'text.secondary' }}
//                                                                 />
//                                                                 <Chip
//                                                                     label={`${policyCount} Policies`}
//                                                                     size="small"
//                                                                     sx={{ ml: 2, fontFamily:'Gilroy-Regular', height: '24px', bgcolor: '#e0e0e0', color: '#333' }}
//                                                                 />
//                                                             </ListItemButton>

//                                                             {/* Collapsible Detail Section for the selected Cluster Result */}
//                                                             <Collapse in={isClusterExpanded} timeout="auto" unmountOnExit>
//                                                                 <Box sx={{ pl: { xs: 2, md: 4 }, pr: { xs: 2, md: 3 }, py: 2, borderTop: '1px solid #eee', bgcolor: 'background.paper', borderBottom: index < results.length - 1 ? '1px solid #eee' : 'none' }}>
//                                                                     <Typography variant="h6" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '1rem', mb: 2.5 }}> Policies Evaluated in: {clusterName} (at {resultDate}) </Typography>

//                                                                     {/* Nested Accordions for Policies */}
//                                                                     {(result?.policies?.length ?? 0) > 0 ? (
//                                                                         result.policies.map((policy: any, policyIndex: number) => {
//                                                                             const policyKey = `${result.id}-${policy.name}`; // Unique key for expansion state
//                                                                             const isPolicyExpanded = expandedPolicyKey === policyKey;
//                                                                             const severityInfo = getSeverityColor(policy.severity);

//                                                                             return (
//                                                                                 <Accordion
//                                                                                     key={policyKey}
//                                                                                     expanded={isPolicyExpanded}
//                                                                                     onChange={() => handlePolicyToggle(policyKey)}
//                                                                                     disableGutters
//                                                                                     elevation={0}
//                                                                                     sx={{
//                                                                                         border: '1px solid #e0e0e0',
//                                                                                         borderRadius: '6px !important', // Ensure radius applies correctly
//                                                                                         boxShadow: 'none',
//                                                                                         '&:before': { display: 'none' },
//                                                                                         mb: 1.5,
//                                                                                         overflow: 'hidden', // Prevent content spill on radius
//                                                                                          bgcolor: isPolicyExpanded ? '#f9f9f9' : 'transparent' // Slight background change when expanded
//                                                                                     }}
//                                                                                 >
//                                                                                     <AccordionSummary
//                                                                                         expandIcon={<ExpandMoreIcon />}
//                                                                                         aria-controls={`${policyKey}-content`}
//                                                                                         id={`${policyKey}-header`}
//                                                                                         sx={{ px: 2, py: 0.5, minHeight: '48px', '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 } }}
//                                                                                     >
//                                                                                          {/* Policy Name and Title */}
//                                                                                         <Box sx={{ flexGrow: 1, mr: 1 }}>
//                                                                                             <Typography fontFamily="Gilroy-Medium" fontSize="0.9rem" component="div">{policy.name}</Typography>
//                                                                                             <Typography fontFamily="Gilroy-Regular" fontSize="0.8rem" color="text.secondary" component="div">{policy.title}</Typography>
//                                                                                         </Box>
//                                                                                         {/* Severity and Action Chips */}
//                                                                                         <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
//                                                                                              <Tooltip title={`Severity: ${policy.severity?.toUpperCase() ?? 'N/A'}`} placement="top">
//                                                                                                 <Chip
//                                                                                                     label={policy.severity?.toUpperCase() ?? 'N/A'}
//                                                                                                     size="small"
//                                                                                                     sx={{
//                                                                                                          bgcolor: severityInfo.color,
//                                                                                                          color: '#fff', // White text for better contrast on colored bg
//                                                                                                          height: '22px',
//                                                                                                          fontSize: '0.75rem',
//                                                                                                          fontFamily: 'Gilroy-SemiBold'
//                                                                                                     }}
//                                                                                                 />
//                                                                                              </Tooltip>
//                                                                                              <Tooltip title={`Action on Failure: ${policy.validationFailureAction ?? 'N/A'}`} placement="top">
//                                                                                                  <Chip
//                                                                                                     label={policy.validationFailureAction ?? 'N/A'}
//                                                                                                     icon={<InfoOutlinedIcon sx={{ fontSize: '1rem', ml: '4px', mr: '-2px' }} />}
//                                                                                                     size="small"
//                                                                                                     variant="outlined"
//                                                                                                     sx={{ height: '22px', fontSize: '0.75rem', fontFamily: 'Gilroy-Regular', borderColor: '#bdbdbd', color: '#616161' }}
//                                                                                                 />
//                                                                                              </Tooltip>
//                                                                                         </Stack>
//                                                                                     </AccordionSummary>
//                                                                                     <AccordionDetails sx={{ px: 2, pt: 1, pb: 2, borderTop: '1px solid #eee' }}>
//                                                                                          {/* Policy Description */}
//                                                                                         <Typography variant="body2" sx={{ fontFamily: 'Gilroy-Regular', fontSize: '0.85rem', color: 'text.secondary', mb: 2, whiteSpace: 'pre-wrap' }}>
//                                                                                              {policy.description || 'No description provided.'}
//                                                                                         </Typography>

//                                                                                          {/* Rules List */}
//                                                                                         <Typography variant="subtitle2" sx={{ fontFamily: 'Gilroy-SemiBold', fontSize: '0.85rem', mb: 1 }}> Rules: </Typography>
//                                                                                         <List dense disablePadding sx={{ border: '1px solid #eee', borderRadius: '4px', bgcolor: '#fff' }}>
//                                                                                             {(policy?.rules?.length ?? 0) > 0 ? (
//                                                                                                 policy.rules.map((rule: any, ruleIndex: number) => (
//                                                                                                     <ListItem key={`${policyKey}-rule-${rule.name ?? ruleIndex}`} divider={ruleIndex < (policy.rules.length - 1)}>
//                                                                                                         <ListItemText
//                                                                                                             primary={rule.name || 'Unnamed Rule'}
//                                                                                                             secondary={rule.message || 'No message provided.'}
//                                                                                                             primaryTypographyProps={{ fontFamily: 'Gilroy-Medium', fontSize: '0.85rem', mb: 0.2 }}
//                                                                                                             secondaryTypographyProps={{ fontFamily: 'Gilroy-Regular', fontSize: '0.8rem', color: 'text.secondary', whiteSpace: 'pre-wrap' }}
//                                                                                                         />
//                                                                                                     </ListItem>
//                                                                                                 ))
//                                                                                             ) : ( <ListItem><ListItemText primary="No specific rules listed for this policy." sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic' }} primaryTypographyProps={{ fontSize: '0.8rem' }} /></ListItem> )}
//                                                                                         </List>
//                                                                                     </AccordionDetails>
//                                                                                 </Accordion>
//                                                                             );
//                                                                         })
//                                                                     ) : (
//                                                                         <Typography sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', fontFamily: 'Gilroy-Regular', fontSize: '0.9rem', mt: 2, mb: 1 }}> No policies found in this result set. </Typography>
//                                                                     )}
//                                                                 </Box>
//                                                             </Collapse>
//                                                         </React.Fragment>
//                                                     );
//                                                 })}
//                                             </List>
//                                         </AccordionDetails>
//                                     </Accordion>
//                                 );
//                             })}
//                         </Box>
//                     </>
//                 )}
//             </Box>
//         </Container>
//     );
// };

// export default KyvernoDetails;
// import { useNavigate } from "react-router-dom";
// import React, { useState } from "react";
// import {
// 	Autocomplete,
// 	Box,
// 	Breadcrumbs,
// 	Button,
// 	Container,
// 	Divider,
// 	Grid,
// 	IconButton,
// 	Link,
// 	TextField,
// 	Typography,
// } from "@mui/material";
// import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
// import { ErrorMessage, Form, Formik, getIn } from "formik";
// import * as Yup from "yup";
// import CompulsoryFieldsLabel from "../../helpers/CompulsoryFields";
// import queryGraphql from "../../helpers/ApiInterface";
// import { useAuth } from "../../helpers/AuthProvider";
// import MainDrawer from "../MainDrawer";

// const KyvernoDetails: React.FC =  () => {
//     return(<Box sx={{ display: "flex"}}>
//         {/* <MainDrawer index={"aisolutions-rag"} /> */}
    
//         <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100vh", width:"100vw"}}>
//      <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography>
//         </Box>
//         </Box>)
    
//     }
    
// export default KyvernoDetails;
    